import React, { useContext, useState } from 'react';
import proptypes from 'prop-types';
const { bool } = proptypes;
import {
  FormController,
  FormStatusMessage,
  Typography,
  TextField,
  IconButton,
  Alert,
  Link
} from '@one-thd/sui-atomic-components';
import { Search } from '@one-thd/sui-icons';
import cx from 'classnames';
import { StoreSearchContext } from '../StoreSearch.context';
import { analytics } from './analytics';

const ERROR_MESSAGE_EMPTY = 'Please enter a valid ZIP Code, City, State, or Store #.';
const PLACEHOLDER_TEXT = 'ZIP Code, City, State, or Store #';
const ALERT_GEOLOCATION_DISABLED_MESSAGE = 'We\'re sorry, we are unable to access your current location.';

export const SearchInput = ({ hideUseCurrentLocation = false, showStoreDirectoryLink = false }) => {
  const {
    setSearchCoordinates,
    setSearchInput,
    setShowLoader,
    searchSuccessCallback
  } = useContext(StoreSearchContext);

  const [showEmptyError, setShowEmptyError] = useState(false);
  const [searchBoxValue, setSearchBoxValue] = useState('');
  const [isDisabledGeolocation, setIsDisabledGeolocation] = useState(false);

  const handleInputChange = (event) => {
    setShowEmptyError(false);
    setSearchBoxValue(event?.target?.value);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    event.nativeEvent.stopImmediatePropagation();
    if (searchBoxValue?.length === 0) {
      setShowEmptyError(true);
    } else {
      setShowEmptyError(false);
      setSearchCoordinates({});
      setSearchInput(searchBoxValue);
      analytics.triggerAnalytics();
      searchSuccessCallback();
    }
  };

  const onGeolocateError = () => {
    setShowLoader(false);
    setIsDisabledGeolocation(true);
  };

  const onGeolocateSuccess = (position) => {
    const { latitude, longitude } = position?.coords || {};
    setSearchCoordinates({ lat: latitude, lng: longitude });
    setShowLoader(false);
    setIsDisabledGeolocation(false);
    searchSuccessCallback();
  };

  const getUserLocation = () => {
    setSearchBoxValue('');
    setShowLoader(true);
    setIsDisabledGeolocation(false);
    const geolocation = typeof window !== 'undefined' ? window.navigator?.geolocation : undefined;

    if (geolocation) {
      analytics.triggerAnalytics();
      geolocation.getCurrentPosition(onGeolocateSuccess, onGeolocateError);
    }
  };

  const wrapperLinksClasses = cx({
    'sui-flex sui-justify-between sui-pt-2 sui-pb-2': !hideUseCurrentLocation && showStoreDirectoryLink,
    'sui-grid sui-pt-2 sui-pb-2': !hideUseCurrentLocation && !showStoreDirectoryLink,
    'sui-flex sui-justify-center sui-pt-2 sui-pb-2': hideUseCurrentLocation && showStoreDirectoryLink,
  });

  return (
    <div className="sui-w-full" data-component="SearchInput">
      <form className="sui-grid" onSubmit={onSubmit} data-testid="store-search-form">
        <FormController>
          <TextField
            data-testid="store-search-text-field"
            status={showEmptyError ? 'error' : undefined}
            onChange={handleInputChange}
            placeholder={PLACEHOLDER_TEXT}
            value={searchBoxValue}
            InputProps={{
              endAdornment: (
                <IconButton buttonSize="tight" iconSize="small" icon={Search} onClick={onSubmit} color="primary" />
              )
            }}

          />
          {showEmptyError && (
            <FormStatusMessage status="error">{ERROR_MESSAGE_EMPTY}</FormStatusMessage>
          )}
        </FormController>
      </form>

      <div className={wrapperLinksClasses}>
        {!hideUseCurrentLocation && (
          <button
            onClick={getUserLocation}
            type="button"
          >
            <Typography decoration="underline">Use Current Location</Typography>
          </button>
        )}
        {showStoreDirectoryLink && (
          <Link
            href="https://www.homedepot.com/l/storeDirectory"
            data-testid="store-search-link-store-directory"
          >
            Store Directory
          </Link>
        )}
      </div>

      {isDisabledGeolocation && (
        <Alert status="error" data-testid="store-search-alert-geolocation">
          <span className="sui-flex sui-gap-1">
            <Typography>
              {ALERT_GEOLOCATION_DISABLED_MESSAGE}
            </Typography>
          </span>
        </Alert>
      )}
    </div>
  );
};

SearchInput.propTypes = {
  hideUseCurrentLocation: bool,
  showStoreDirectoryLink: bool
};

SearchInput.displayName = 'SearchInput';